import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layers, Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Header({ onLogin }) {
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navLinks = [
    { path: "/funkcje", labelKey: "header.features", isAnchor: false },
    { path: "/blog", labelKey: "header.blog", isAnchor: false },
    { path: "/shop-public", labelKey: "header.shop", isAnchor: false },
  ];

  // Dostępne języki - łatwo rozszerzalne
  const availableLanguages = [
    { code: "pl", label: "PL", flag: "🇵🇱" },
    { code: "en", label: "EN", flag: "🇬🇧" },
    // Dodaj nowe języki tutaj:
    // { code: "de", label: "DE", flag: "🇩🇪" },
    // { code: "es", label: "ES", flag: "🇪🇸" },
  ];

  return (
    <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-lg border-b border-slate-100 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Layers className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Epidemos</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <a 
                  key={link.path}
                  href={link.path} 
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {t(link.labelKey)}
                </a>
              ) : (
                <Link 
                  key={link.path}
                  to={link.path} 
                  className={`text-sm transition-colors ${
                    isActive(link.path) 
                      ? "text-blue-600 font-semibold" 
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              )
            ))}

            {/* Language Switcher - rozszerzalny */}
            <div className="flex items-center gap-1 ml-4 border-l pl-4 border-slate-200">
              <Globe className="w-4 h-4 text-slate-400 mr-1" />
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    language === lang.code
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  }`}
                  title={lang.code === 'pl' ? 'Polski' : 'English'}
                  data-testid={`lang-switch-${lang.code}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Auth Buttons */}
            <Button variant="outline" size="sm" onClick={onLogin} className="ml-2" data-testid="header-login-btn">
              {t("header.login")}
            </Button>
            <Button size="sm" onClick={onLogin} data-testid="header-register-btn">
              {t("header.register")}
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenu(!mobileMenu)}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
          >
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenu && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <div className="space-y-3">
              {navLinks.map((link) => (
                link.isAnchor ? (
                  <a
                    key={link.path}
                    href={link.path}
                    className="block text-sm text-slate-600 hover:text-slate-900 py-2"
                    onClick={() => setMobileMenu(false)}
                  >
                    {t(link.labelKey)}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block text-sm py-2 ${
                      isActive(link.path)
                        ? "text-blue-600 font-semibold"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                    onClick={() => setMobileMenu(false)}
                  >
                    {t(link.labelKey)}
                  </Link>
                )
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="flex items-center gap-2 py-2 border-t border-slate-100 mt-2 pt-3">
                <Globe className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500 mr-2">{language === 'pl' ? 'Język:' : 'Language:'}</span>
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      language === lang.code
                        ? "bg-blue-50 text-blue-600"
                        : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 pt-3">
                <Button variant="outline" size="sm" onClick={onLogin} className="flex-1">
                  {t("header.login")}
                </Button>
                <Button size="sm" onClick={onLogin} className="flex-1">
                  {t("header.register")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
